import React from "react";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AndroidIcon from "@mui/icons-material/Android";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { Themed, PHONE } from "./Walkthrough";

// Cards for the Projects page. The project name is the card's link, stretched over the whole
// card, so the whole card is clickable while its buttons stay separate links on top of it.

const cardBase =
  "group relative overflow-hidden rounded-3xl bg-white dark:bg-[#2f3942] ring-1 ring-black/5 dark:ring-white/10 " +
  "shadow-[0_2px_10px_rgba(0,0,0,0.06)] transition duration-200 hover:-translate-y-1 hover:shadow-[0_16px_36px_-12px_rgba(0,0,0,0.25)] " +
  "motion-reduce:transition-none motion-reduce:hover:translate-y-0";

// Focus ring drawn on the stretched overlay, so keyboard focus outlines the whole card.
const stretchedLink =
  "focus:outline-none [&:focus-visible>span]:ring-4 [&:focus-visible>span]:ring-[#eb4d5c] dark:[&:focus-visible>span]:ring-[#c9a7ff]";

const smallFilled =
  "relative z-10 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[15px] font-semibold bg-[#eb4d5c] text-white hover:bg-[#d63f4e] dark:bg-[#c9a7ff] dark:text-[#0f193d] dark:hover:bg-[#b48ff5] transition-colors";
const smallOutline =
  "relative z-10 inline-flex items-center gap-1.5 rounded-full px-4 py-[6px] text-[15px] font-semibold border-2 border-[#eb4d5c] text-[#d63c4b] hover:bg-[#eb4d5c] hover:text-white dark:border-[#c9a7ff] dark:text-[#c9a7ff] dark:hover:bg-[#c9a7ff] dark:hover:text-[#0f193d] transition-colors";

const eyebrowClass = "text-[13px] font-semibold uppercase tracking-[0.14em] text-[#d63c4b] dark:text-[#c9a7ff]";

function projectUrl(project) {
  return project.link || `/projects/${project.id}`;
}

function TechList({ tech, max }) {
  const shown = max ? tech.slice(0, max) : tech;
  const extra = tech.length - shown.length;
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label="Built with">
      {shown.map((t) => (
        <li key={t} className="rounded-full bg-black/[0.05] dark:bg-white/10 px-2.5 py-0.5 text-[13px] font-medium text-[#444] dark:text-[#d6d9e3]">
          {t}
        </li>
      ))}
      {extra > 0 && (
        <li className="px-1 py-0.5 text-[13px] font-medium text-[#666] dark:text-[#aab1c4]">+{extra} more</li>
      )}
    </ul>
  );
}

// Two screenshots in phone frames, rising out of a tinted panel and cropped at its bottom edge.
function ShotPanel({ card, image, name }) {
  return (
    <div
      className={`relative h-[250px] sm:h-[300px] lg:h-full lg:min-h-[400px] overflow-hidden ${card.tint || "bg-[#f3f0ee] dark:bg-[#26303a]"}`}
    >
      {card.shots ? (
        <div className="absolute inset-x-0 top-8 lg:top-10 flex justify-center gap-4 lg:gap-5">
          {card.shots.map((shot, i) => (
            <div
              key={i}
              className={`w-[132px] sm:w-[160px] lg:w-[180px] transition-transform duration-300 motion-reduce:transition-none ${
                i === 1 ? "mt-8 lg:mt-12 group-hover:-translate-y-2" : "group-hover:-translate-y-1"
              }`}
            >
              <div className="rounded-[1.4rem] bg-[#16171b] p-[4px] shadow-[0_18px_40px_-12px_rgba(0,0,0,0.45)] ring-1 ring-black/10 dark:ring-white/15">
                <Themed
                  shot={shot}
                  alt={shot.alt}
                  width={PHONE.width}
                  height={PHONE.height}
                  className="w-full h-auto rounded-[1.15rem]"
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <img src={image} alt="" aria-hidden="true" className="absolute inset-0 m-auto max-h-[70%] max-w-[70%] object-contain" />
      )}
      <span className="sr-only">{name}</span>
    </div>
  );
}

export function FeaturedProject({ project, reverse = false }) {
  const { card } = project;
  const url = projectUrl(project);

  return (
    <article
      className={`${cardBase} grid ${
        reverse ? "lg:grid-cols-[minmax(0,6fr)_minmax(0,5fr)]" : "lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]"
      }`}
    >
      <div className={reverse ? "lg:order-2 lg:col-start-2" : ""}>
        <ShotPanel card={card} image={project.image} name={project.name} />
      </div>

      <div className="flex flex-col gap-4 p-6 sm:p-8 lg:p-10 lg:justify-center">
        <div className="flex items-center gap-3">
          {card.icon && (
            <img
              src={card.icon}
              alt=""
              aria-hidden="true"
              className={card.iconWide ? "h-11 w-auto rounded-xl shadow-sm" : "h-11 w-11 drop-shadow-sm"}
            />
          )}
          <p className={eyebrowClass}>{card.eyebrow}</p>
        </div>

        <h2 className="text-[28px] lg:text-[34px] font-bold leading-tight text-[#111] dark:text-white">
          <Link to={url} className={stretchedLink}>
            <span className="absolute inset-0 rounded-3xl" aria-hidden="true" />
            {project.name}
          </Link>
        </h2>

        <p className="text-[17px] lg:text-lg leading-relaxed text-[#3a3a3a] dark:text-[#d6d9e3]">{card.pitch}</p>

        <TechList tech={card.tech} max={5} />

        <div className="flex flex-wrap items-center gap-2.5 pt-2">
          {card.primary ? (
            <Link to={card.primary.to} className={smallFilled}>
              {card.primary.play && <PlayArrowRoundedIcon fontSize="small" aria-hidden="true" />}
              {card.primary.label}
              {!card.primary.play && <ArrowForwardIcon fontSize="small" aria-hidden="true" />}
            </Link>
          ) : (
            <Link to={url} className={smallFilled} tabIndex={-1} aria-hidden="true">
              See the walkthrough
              <ArrowForwardIcon fontSize="small" aria-hidden="true" />
            </Link>
          )}
          {/* A second button to the walkthrough, unless the main button already opens that page */}
          {card.primary && card.primary.to.split("#")[0] !== url && (
            <Link to={url} className={smallOutline}>
              See the walkthrough
            </Link>
          )}
          {card.apk && (
            <a href={`https://github.com/${card.apk.repo}/releases/latest/download/${card.apk.file}`} className={smallOutline}>
              <AndroidIcon sx={{ fontSize: 18 }} aria-hidden="true" />
              Download APK
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

export function CompactProject({ project }) {
  const { card } = project;
  return (
    <article className={`${cardBase} flex flex-col gap-4 p-5 sm:flex-row sm:gap-5 sm:p-6`}>
      <div className="flex h-16 w-16 sm:h-24 sm:w-24 shrink-0 items-center justify-center rounded-2xl bg-[#f3f0ee] dark:bg-[#26303a]">
        <img src={project.image} alt="" aria-hidden="true" className="max-h-[70%] max-w-[70%] object-contain" />
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        <p className={eyebrowClass}>{card.eyebrow}</p>
        <h3 className="text-[22px] font-bold leading-tight text-[#111] dark:text-white">
          <Link to={projectUrl(project)} className={stretchedLink}>
            <span className="absolute inset-0 rounded-3xl" aria-hidden="true" />
            {project.name}
          </Link>
        </h3>
        {card.badge && (
          <p className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#fde3e5] px-3 py-1 text-[13px] font-semibold text-[#b8303f] dark:bg-[#c9a7ff]/20 dark:text-[#d9c2ff]">
            {card.badgeIcon === "team" ? (
              <GroupsOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
            ) : (
              <ArticleOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
            )}
            {card.badge}
          </p>
        )}
        <p className="text-[16px] leading-relaxed text-[#3a3a3a] dark:text-[#d6d9e3]">{card.pitch}</p>
        <TechList tech={card.tech} />
      </div>
    </article>
  );
}
